<!--
 * @Description: 首页商机分析漏斗图组件
 * @Author: mdb
 * @Date: 2023-03-16 11:13:11
 * @LastEditTime: 2023-03-16 18:12:09
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <CosRangePicker
          v-model:startTime="queryForm.startTime"
          v-model:endTime="queryForm.endTime"
          :allowClear="props.allowClear"
          @change="getData"
        />
        <CosSelect
          v-model:value="queryForm.depCode"
          :allowClear="props.allowClear"
          style="width: 145px; margin-left: 10px"
          @change="getData"
        >
          <a-select-option value="all">{{ props.isMultiLanguage ? t('home.jio30c') : '全部数据' }}</a-select-option>
          <a-select-option value="my">{{ props.isMultiLanguage ? t('home.dd13al') : '我的数据' }}</a-select-option>
        </CosSelect>
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeMount, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useECharts } from '@/hooks/useECharts'
import { getCurrentMonthDateRange, toBeijingTime } from '@/utils/timeFormat'
import { useInteractionStore } from '@/stores/interaction'
import { chanceAnalysis as chanceAnalysisFbu } from '@/api/gucangSales/index'
import { chanceAnalysis as chanceAnalysisOfbu } from '@/api/overseasSales/index'
import { chanceAnalysis as chanceAnalysisLbu } from '@/api/yuntuSales/index'
import type { Ref, PropType } from 'vue'
interface QueryFormType {
  depCode: string
  startTime: string
  endTime: string
}
interface ItemCommonType {
  salesStageStr: string
  salesCount: number
  salesStage: number
}

const { t } = useI18n()
const { language } = storeToRefs(useInteractionStore())
const { firstDayOfMonth, currentDayOfMonth } = getCurrentMonthDateRange()

const props = defineProps({
  title: {
    type: String as PropType<string>
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_ofbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要转换时区
  isTimeZoneConv: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要支持多语言
  isMultiLanguage: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const lbuEnum = [
  { value: 100, name: '潜力商机', itemStyle: { color: '#3F97FF' } },
  { value: 88, name: '首次接触', itemStyle: { color: '#54A0FF' } },
  { value: 72, name: '提供方案&谈判', itemStyle: { color: '#67AAFF' } },
  // { value: 60, name: '签约', itemStyle: { color: '#7AB5FF' } },
  { value: 48, name: '签约&准备发件', itemStyle: { color: '#8DBFFF' } },
  { value: 36, name: '首次发件', itemStyle: { color: '#A0CAFF' } },
  { value: 24, name: '达到承诺业务量', itemStyle: { color: '#B3D5FF' } },
  { value: 12, name: '未来机会', itemStyle: { color: '#C6DFFF' } },
  { value: 0, name: '不能合作', itemStyle: { color: '#D9EAFF' } }
]
const fbuEnum = [
  { value: 100, name: '初步接洽', itemStyle: { color: '#3F97FF' } },
  { value: 82, name: '确认需求', itemStyle: { color: '#58A6FF' } },
  { value: 66, name: '制定方案', itemStyle: { color: '#72B2FF' } },
  { value: 50, name: '达成合作', itemStyle: { color: '#8CC0FF' } },
  { value: 34, name: '未能合作', itemStyle: { color: '#A5CEFF' } },
  { value: 18, name: '完结', itemStyle: { color: '#BFDFFF' } },
  { value: 0, name: '关闭', itemStyle: { color: '#D9EAFF' } }
]
const ofbuEnum = [
  { value: 100, name: t('enum.businessStatus1'), itemStyle: { color: '#3F97FF' } },
  { value: 75, name: t('enum.businessStatus2'), itemStyle: { color: '#66ACFF' } },
  { value: 45, name: t('enum.businessStatus5'), itemStyle: { color: '#B2D6FF' } },
  { value: 0, name: t('enum.businessStatus6'), itemStyle: { color: '#D9EAFF' } }
]
// 函数样式枚举
const styleEnum = {
  cos_fbu: fbuEnum,
  cos_ofbu: ofbuEnum,
  cos_lbu: lbuEnum
}
const title = computed(() => {
  return props.title || props.isMultiLanguage ? t('home.2b1akk') : '商机分析'
})
// 函数枚举
const functionEnum = {
  cos_fbu: chanceAnalysisFbu,
  cos_ofbu: chanceAnalysisOfbu,
  cos_lbu: chanceAnalysisLbu
}
const spinning = ref(false)
const showECharts = ref(false)
const queryForm = reactive<QueryFormType>({
  depCode: 'all',
  startTime: firstDayOfMonth,
  endTime: currentDayOfMonth
})
const getData = () => {
  spinning.value = true
  const params = Object.assign({}, queryForm)
  if (props.isTimeZoneConv) {
    params.startTime = toBeijingTime(params.startTime)
    params.endTime = toBeijingTime(params.endTime)
  }
  functionEnum[props.appId](params)
    .then((res) => {
      const data = res.data as unknown as ItemCommonType[]
      if (data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        showECharts.value = true
        initChart(data)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemCommonType[]) => {
  const labelLine = data.map((item) => ({
    value: 0,
    name: item.salesStageStr,
    itemStyle: { color: '#999' }
  }))
  // 当返回 6 种状态时，需要特殊处理（lbu）
  const flag = data.length === 6
  const funnelList = data.map((item, index) => ({
    value: styleEnum[props.appId][index].value,
    name: item.salesStageStr,
    itemStyle: styleEnum[props.appId][index].itemStyle
  }))
  setOptions({
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {c}'
    },
    series: [
      {
        type: 'funnel',
        left: 455,
        top: 30,
        bottom: 60,
        width: 0,
        min: 0,
        max: 0,
        minSize: 0,
        maxSize: 0,
        sort: 'none',
        z: 1,
        gap: 2,
        label: {
          show: true,
          position: 'left',
          formatter: (params) => {
            const label = params.name
            if (label === 'Contacted' || label === 'Close Won') return label
            if (label === 'Custom Solution: Including  Quote & Pricing') {
              return label.replace(/:\s*/, ':\n')
            }
            if (label === 'Account Needs Confirmation') {
              return label.replace(/(\w+\s+\w+)\s(\w+)/, '$1\n$2')
            }
            return label
          }
        },
        labelLine: {
          length: 300,
          lineStyle: {
            width: 1,
            type: 'dashed',
            color: '#ccc'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: () => ''
        },
        data: labelLine
      },
      {
        type: 'funnel',
        left: 150,
        top: 30,
        bottom: 60,
        width: '70%',
        min: 20,
        max: 100,
        minSize: '10%',
        maxSize: '100%',
        sort: 'none',
        gap: 2,
        label: (props.appId === 'cos_ofbu')
          ? {
              show: true,
              position: 'inside',
              color: '#fff',
              formatter: (params) => {
                const { name } = params
                const item = data.find((item) => item.salesStageStr === name)
                return item?.salesCount.toString() || ''
              }
            }
          : { show: false },
        emphasis: {
          label: { show: false }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const { name } = params
            const item = data.find((item) => item.salesStageStr === name)
            return `${name} : ${item?.salesCount}` || ''
          }
        },
        data: funnelList
      }
    ]
  })
}
onBeforeMount(() => getData())
if (props.isMultiLanguage) {
  watch(
    () => language.value,
    () => getData()
  )
}
</script>
<script lang="ts">
export default { name: 'HomeOpportunityFunnel' }
</script>

<style scoped></style>
